import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    password?: string;
    usernamePos?: string;
    employeeNumber?: string;
    socialSecurityNumber?: string;
    startDate?: string;
    isActive: boolean;
    isAllowedToViewCommonReports?: boolean;
    usesTimeStamping?: boolean;
    salaryType?: string;
    role: Role | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    locations: Location[];
    brands: Brand[];
}

export interface Role {
    id: number;
    roleName: string;
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface Address {
    id?: number;
    addressType: AddressType;
    addressTypeId?: number;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    [key: string]: unknown;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
    systemTypeName?: string;
}

export interface ContactInfo {
    id?: number;
    contactInfoType: ContactInfoType;
    value?: string;
    [key: string]: unknown;
}

export interface Location {
    id: number;
    locationNumber?: number;
    locationName: string;
}

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
}

// API calls

export async function getUser(userId: number): Promise<User> {
    return legacyFetch<User>(`users/${userId}`);
}

export async function createUser(user: Partial<User>): Promise<User> {
    return legacyFetch<User>('users', {
        method: 'POST',
        body: JSON.stringify(user),
    });
}

export async function updateUser(userId: number, user: Partial<User>): Promise<User> {
    return legacyFetch<User>(`users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(user),
    });
}

export async function getAddressTypes(): Promise<AddressType[]> {
    return legacyFetch<AddressType[]>('users/addresstypes');
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    return legacyFetch<ContactInfoType[]>('users/contactinfotypes');
}

export async function getValidRoles(): Promise<Role[]> {
    return legacyFetch<Role[]>('users/validuserroles');
}

export async function getLocations(): Promise<Location[]> {
    return legacyFetch<Location[]>('users/locations');
}

export async function getBrands(): Promise<Brand[]> {
    return legacyFetch<Brand[]>('users/brands');
}
